import { couldBeJson, getViewportDimensions } from '@ribajs/utils'
import { parseUnit } from './number.js'
import { HttpServiceResponse } from '@ribajs/core'

export const SHOPIFY_APP_SCRIPTS_READY_EVENT_NAME = 'shopify-app-scripts:ready'

export const originalWindowAttachEvent = window.attachEvent?.bind(window)
export const originalWindowAddEventListener =
  window.addEventListener.bind(window)

/**
 * Determine if an element is in the viewport
 * @param elem The element
 * @param offset.top Distance to the top of the screen, if this is 0, the element must be scrolled until the top of the screen
 * @param offset.bottom Distance to the bottom of the screen, if this is 0, the scroll position must be over the element
 * @return Returns true if element is in the viewport
 */
export const isInViewport = (
  elem: Element,
  offset: {
    top?: number
    bottom?: number
    left?: number
    right?: number
  } = {},
) => {
  if (!elem) {
    return false
  }

  const distance = elem.getBoundingClientRect()

  const vp = getViewportDimensions()

  offset.top ||= 0
  offset.left ||= 0
  offset.bottom ||= 0
  offset.right ||= 0

  offset.top += vp.h - distance.height
  offset.left += vp.w - distance.width

  const vertical =
    distance.top + distance.height >= offset.bottom &&
    distance.bottom - distance.height <= offset.top

  const horizontal =
    distance.left + distance.width >= offset.right &&
    distance.right - distance.width <= offset.left

  return vertical && horizontal
}

/**
 * Load a script file by source
 * @param {} src
 * @param {} attributes Additional attributes
 * @param {*} callback
 */
export const loadScript = async (
  src: string,
  options: {
    type?: string
    async?: boolean
    defer?: boolean
    id?: string
  } = {},
  attributes: Record<string, string> = {},
  callback?: (error: any | null, script?: HTMLScriptElement) => void,
) => {
  const script = await new Promise<HTMLScriptElement>((resolve, reject) => {
    if (options.type === undefined) options.type = 'module'
    if (options.async === undefined) options.async = true
    if (options.defer === undefined) options.defer = false

    const script = document.createElement('script')
    if (options.type) script.type = options.type
    if (options.async) script.async = options.async
    if (options.defer) script.defer = options.defer
    if (options.id) script.id = options.id

    for (const attrKey of Object.keys(attributes)) {
      script.setAttribute(attrKey, attributes[attrKey])
    }

    const timeout = setTimeout(() => {
      const error = new Error('timeout!')
      if (callback) callback(error, script)
      reject(error)
    }, 5000)

    script.onerror = error => {
      clearTimeout(timeout)
      if (callback) callback(error, script)
      reject(error)
    }

    script.onload = () => {
      clearTimeout(timeout)
      if (callback) callback(null, script)
      resolve(script)
    }

    script.src = src
    document.head.appendChild(script)
  })

  return script
}

/**
 * Parse a string with a unit to a number.
 * @example
 * ```ts
 *  parseUnit('10px') // {unit: 'px', val: 10}
 *  parseUnit('10') // {unit: 'px', val: 10}
 *  parseUnit('10vw') // {unit: 'vw', val: 120}
 * ```
 * @param value The string or number to parse
 * @returns The parsed values
 */
export const parseSizeUnit = (value: string | number) => {
  if (typeof value === 'number') {
    return {
      unit: 'px',
      val: value,
      originalVal: value,
      original: value,
    }
  }

  const { unit, val } = parseUnit(value)

  let result = val

  switch (unit) {
    case 'px':
      result = val
      break
    case 'vw':
      result = (val / 100) * window.innerWidth
      break
    case 'vh':
      result = (val / 100) * window.innerHeight
      break
    default:
      console.error('Unsupported unit', unit)
  }

  return {
    unit,
    val: result,
    originalVal: val,
    original: value,
  }
}

/**
 * Parse the json string from a Shopify response
 * @param res The response
 * @returns The parsed json
 */
export const responseSectionToJson = async (
  res: HttpServiceResponse<string>,
) => {
  const template = document.createElement('template') as HTMLTemplateElement
  template.innerHTML = res.body

  const jsonStr = template.content.querySelector('section')?.innerHTML.trim()

  if (!jsonStr) {
    console.warn("Can't parse collection response!", res)
    return null
  }

  let data: any = null

  if (couldBeJson(jsonStr)) {
    try {
      data = JSON.parse(jsonStr)
    } catch (error) {
      console.error(error)
      console.error(jsonStr)
    }
  }

  return data || null
}
